import React from 'react';
import { useIntl } from 'react-intl';
import { Virtuoso } from 'react-virtuoso';
import { GenericMessages } from '../../intl/GenericMessages';
import { Controller } from '../../models/controller';
import { Firmware } from '../../models/firmware';
import { Organization } from '../../models/organization';
import { getFirmwareVersion } from '../../utils/helpers/referenceHelpers';
import { ControllerCard } from '../controller-card/ControllerCard';
import { getFirmwareDisplayFromFirmwareInfo } from 'src/utils/helpers/firmwareHelpers';
import './MobileControllerGrid.scss';

interface MobileControllersGridProps {
  /* The firmwares for the controllers listed. */
  firmwares: Firmware[];
  /** The controllers retrieved from the API.*/
  controllers: Controller[];
  /** The organizations used to provide detail on controller cards */
  organizations: Organization[];
  /** The total number of controllers in the list (including those not yet loaded). */
  totalCount: number;
}

interface GridItem {
  _id: string;
  physicalSerial: string;
  softwareSerial: string;
  firmwareDisplayName?: string;
}

const renderItem = (index: number, item: GridItem) => {
  return (
    <div className="controller-card-grid-item" key={`ControllerCard${item.softwareSerial}`}>
      <ControllerCard
        routerLink={`controllers/${item.softwareSerial}`}
        physicalSerialNumber={item.physicalSerial}
        firmwareDisplayName={item.firmwareDisplayName}
      />
    </div>
  );
};

/**
 * Component that handles listing controllers from the API - NOT from available bluetooth connections.
 */
export const MobileControllersGrid: React.FC<MobileControllersGridProps> = ({ firmwares, controllers, totalCount }) => {
  const intl = useIntl();

  const gridItems = React.useMemo<GridItem[]>(() => {
    return controllers.map((controller: Controller) => {
      const firmware = firmwares.find(firmware => firmware._id === controller.fw);
      const releaseType = firmware?.releaseType ?? intl.formatMessage(GenericMessages.unknownPlaceholder);
      const firmwareVersion = getFirmwareVersion(controller.fw, firmwares);
      const displayName = firmware?.displayName;
      const firmwareDisplayName = getFirmwareDisplayFromFirmwareInfo(intl, firmwareVersion, releaseType, displayName);

      const gridItem: GridItem = {
        _id: controller._id,
        physicalSerial: controller.physicalSerial,
        softwareSerial: controller.ssn,
        firmwareDisplayName: firmwareDisplayName,
      };
      return gridItem;
    });
  }, [controllers, firmwares, intl]);

  return <Virtuoso data={gridItems} itemContent={renderItem} totalCount={totalCount} />;
};
